import { get, merge } from 'lodash'

/**
 * Определяет, нужно ли обрабатывать событие websocket.
 * Событие обрабатывается, когда фильтр равен фильтру по умолчанию
 * либо когда для сервиса включена принудительная обработка.
 *
 * @param {boolean} filterIsEqualToDefault равенство фильтру по умолчанию
 * @param {boolean} alwaysProcessFromWebsocket принудительная обработка
 * @returns {boolean}
 */
function shouldProcessWebsocketEvent(filterIsEqualToDefault, alwaysProcessFromWebsocket) {
  if (!filterIsEqualToDefault && !alwaysProcessFromWebsocket) {
    return false
  }

  return true
}

/**
 * Находит запись в массиве по идентификатору и обновляет или удаляет её.
 * Если передан объект или callback вернул замену, запись обновляется
 * слиянием, иначе запись удаляется из массива.
 *
 * @this {import('./Service').default}
 * @param {Array<object>} result массив записей
 * @param {object|string} item запись или её идентификатор
 * @param {Function} [callback] возвращает замену для найденной записи
 * @returns {Promise<boolean>} признак того, что запись была найдена
 */
export async function findInArrayAndReplaceOrDelete(result, item, callback) {
  let replacement
  let find = item

  if (typeof item !== 'string') {
    replacement = item
    find = get(item, this.idField)
  }

  if (Array.isArray(result) && result.length) {
    const index = result.findIndex(item => {
      return get(item, this.idField) === find
    })
    if (index > -1) {
      if (typeof callback === 'function') {
        replacement = await callback()
      }
      if (replacement !== undefined) {
        merge(result[index], replacement)
      } else {
        result.splice(index, 1)
      }

      return true
    }
  }

  return false
}

/**
 * Обрабатывает событие создания записи.
 * Увеличивает общее количество записей и добавляет запись в начало
 * списка или буфера. Когда режим догрузки выключен, массив обрезается
 * до размера страницы.
 *
 * @this {import('./Service').default}
 * @param {object} instance созданная запись
 * @returns {Promise<void>}
 */
export async function defaultWebsocketFindCreatedEvent(instance) {
  const shouldProcessEvent = shouldProcessWebsocketEvent(this.findData.filterIsEqualToDefault, this.options.find.alwaysCreateFromWebSocket)
  if (!shouldProcessEvent) {
    return
  }

  this.findData.pagination.total++

  let workingArray = this.findData.data
  let limit = this.findData.pagination.limit
  if (this.options.find.bucketEnabled) {
    workingArray = this.bucket
    limit = this.options.find.bucketMaxLength
  } else {
    instance = await this.options.inputFilter.call(this.ctx, instance)
  }

  if (workingArray) {
    const index = workingArray.findIndex(item => {
      return item.id === instance.id
    })
    if (!~index) {
      workingArray.unshift(instance)
    }

    if (workingArray.length > limit && !this.options.find.appendMode) {
      workingArray.splice(limit)
    }

    if (workingArray === this.bucket) {
      this.findData.bucketLength = this.bucket.length
    }
  }
}

/**
 * Обрабатывает событие обновления записи.
 * Сначала запись ищется в буфере, и только если там её нет, обновляется
 * запись основного списка.
 *
 * @this {import('./Service').default}
 * @param {object} instance обновлённая запись
 * @returns {Promise<void>}
 */
export async function defaultWebsocketFindUpdatedEvent(instance) {
  const shouldProcessEvent = shouldProcessWebsocketEvent(this.findData.filterIsEqualToDefault, this.options.find.alwaysUpdateFromWebSocket)
  if (!shouldProcessEvent) {
    return
  }

  let alreadyProcessed = false
  if (this.options.find.bucketEnabled) {
    alreadyProcessed = await findInArrayAndReplaceOrDelete.call(this, this.bucket, instance)
  }

  if (!alreadyProcessed) {
    await findInArrayAndReplaceOrDelete.call(
      this,
      this.findData.data,
      instance[this.idField],
      () => {
        return this.options.inputFilter.call(this.ctx, instance)
      }
    )
  }
}

/**
 * Пересчитывает страницу после удаления записи.
 * Если текущее смещение вышло за пределы выборки, происходит переход
 * на последнюю существующую страницу. Если страница опустела, а записи
 * ещё есть, она запрашивается заново отложенным запросом. Неполная,
 * но непустая страница не добирается, чтобы не показать удалённые
 * записи из ещё не обновившегося ответа. В режиме догрузки пагинация
 * не меняется.
 *
 * @this {import('./Service').default}
 * @returns {void}
 */
function recalculateFindDataAfterRemove() {
  if (this.options.find.appendMode) {
    return
  }

  const { limit, offset, total } = this.findData.pagination

  if (offset > 0 && offset >= total) {
    const lastPageIndex = Math.max(Math.ceil(total / limit) - 1, 0)
    this.findData.pagination.offset = lastPageIndex * limit

    return
  }

  let loaded = 0
  if (Array.isArray(this.findData.data)) {
    loaded = this.findData.data.length
  }

  if (loaded === 0 && total > 0) {
    if (typeof this.options.find.debouncedFind === 'function') {
      this.options.find.debouncedFind()
    }
  }
}

/**
 * Обрабатывает событие удаления записи.
 * Уменьшает общее количество записей, удаляет запись из буфера или
 * основного списка и пересчитывает текущую страницу.
 *
 * @this {import('./Service').default}
 * @param {object} instance удалённая запись
 * @returns {Promise<void>}
 */
export async function defaultWebsocketFindRemovedEvent(instance) {
  const shouldProcessEvent = shouldProcessWebsocketEvent(this.findData.filterIsEqualToDefault, this.options.find.alwaysRemoveFromWebSocket)
  if (!shouldProcessEvent) {
    return
  }

  if (this.findData.pagination.total > 0) {
    this.findData.pagination.total--
  }

  let alreadyProcessed = false
  if (this.options.find.bucketEnabled) {
    alreadyProcessed = await findInArrayAndReplaceOrDelete.call(this, this.bucket, instance.id)
  }

  if (!alreadyProcessed) {
    await findInArrayAndReplaceOrDelete.call(this, this.findData.data, instance.id)
  }

  recalculateFindDataAfterRemove.call(this)
}

/**
 * Собирает обработчики websocket-событий поиска для сервиса.
 * Для каждого события берётся обработчик из настроек сервиса,
 * а при его отсутствии - обработчик по умолчанию.
 *
 * @param {object} options настройки сервиса
 * @returns {Array<{event: string, handler: Function}>}
 */
export const generateDefaultWebsocketFindEvents = function(options) {
  const nameViaPoint = options.name.split('/').join('.')
  const websocketFindCreatedEvent = options.find.websocketFindCreatedEvent || defaultWebsocketFindCreatedEvent
  const websocketFindUpdatedEvent = options.find.websocketFindUpdatedEvent || defaultWebsocketFindUpdatedEvent
  const websocketFindRemovedEvent = options.find.websocketFindRemovedEvent || defaultWebsocketFindRemovedEvent

  return [
    {
      event: `${nameViaPoint}.created`,
      handler: websocketFindCreatedEvent
    },
    {
      event: `${nameViaPoint}.updated`,
      handler: websocketFindUpdatedEvent
    },
    {
      event: `${nameViaPoint}.updated2`,
      handler: websocketFindUpdatedEvent
    },
    {
      event: `${nameViaPoint}.removed`,
      handler: websocketFindRemovedEvent
    },
    {
      event: `${nameViaPoint}.removed2`,
      handler: websocketFindRemovedEvent
    }
  ]
}
